import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';

import { isApp } from '@/lib/app';
import { tr, useT } from '@/i18n';
import { isEmbedded } from '@/lib/embed';

import { DotField } from './DotField';
import { DownloadDialog } from './DownloadDialog';
import { ArrowUpRightIcon, BridgeMark, DownloadIcon, GitHubMark } from './Icons';
import { LanguageMenu } from './LanguagePicker';
import styles from './Layout.module.css';
import { AppSettings } from './AppSettings';
import { SOURCE_URL } from './sourceUrl';
import { ThemeSwitch } from './ThemeSwitch';

export { README_URL } from './sourceUrl';

function Logo(): React.JSX.Element {
  return (
    <span className={styles.logo}>
      <BridgeMark />
      Bridge
    </span>
  );
}

/**
 * The header: the brand, then the cases, “Download” (the apps, in a window), the developers page and the
 * source. (The native app has no header: Page.)
 */
/** Two even lines, as on Apple's own site; the button turns them into a cross when the menu is open. */
function MenuGlyph({ open }: { readonly open: boolean }): React.JSX.Element {
  return (
    <svg className={[styles.menuGlyph, open ? styles.menuGlyphOpen : ''].join(' ')} width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M3 7h14" />
      <path d="M3 13h14" />
    </svg>
  );
}

const MENU = [
  { to: '/cases', title: 'nav.cases' },
  { to: '/developers', title: 'nav.developers' },
] as const;

/**
 * The phone's menu, as Apple's site has it: the whole screen under the header, the same surface as the page, the
 * destinations as large words that come in one after another. A choice or Escape closes it; the page under it does
 * not scroll meanwhile.
 */
function PhoneMenu({ onClose, onDownload }: { readonly onClose: () => void; readonly onDownload: () => void }): React.JSX.Element {
  const t = useT();
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const root = document.documentElement;
    root.dataset.menu = 'open';
    return () => {
      window.removeEventListener('keydown', onKey);
      delete root.dataset.menu;
    };
  }, [onClose]);
  return (
    <nav className={styles.menu} id="phone-menu" aria-label={t('nav.menu')} data-testid="phone-menu">
      <ul className={styles.menuList}>
        {MENU.map((item, index) => (
          <li key={item.to} style={{ '--i': index } as React.CSSProperties}>
            <Link to={item.to} className={styles.menuRow} onClick={onClose}>
              {t(item.title)}
            </Link>
          </li>
        ))}
        <li style={{ '--i': MENU.length } as React.CSSProperties}>
          <button
            type="button"
            className={styles.menuRow}
            onClick={() => {
              onClose();
              onDownload();
            }}
          >
            {t('nav.download')}
          </button>
        </li>
      </ul>
    </nav>
  );
}

function Header(): React.JSX.Element {
  const t = useT();
  const [download, setDownload] = useState(false);
  const [menu, setMenu] = useState(false);
  return (
    <header className={styles.header}>
      <Link to="/" className={styles.brand} aria-label={t('nav.home')}>
        <Logo />
        <span className={styles.by}>{t('nav.byPassworkShort')}</span>
      </Link>
      <nav className={styles.nav} aria-label={t('nav.main')}>
        <NavLink to="/cases" className={({ isActive }) => [styles.navLink, styles.navCases, isActive ? styles.navLinkActive : ''].join(' ')} data-testid="nav-cases">
          {t('nav.cases')}
        </NavLink>
        <button
          type="button"
          className={[styles.navLink, styles.navApp].join(' ')}
          aria-haspopup="dialog"
          data-testid="header-app"
          onClick={() => {
            setDownload(true);
          }}
        >
          {/* on a phone the word gives way to a download glyph */}
          <DownloadIcon size={20} className={styles.navAppGlyph} />
          <span className={styles.navAppText}>{t('nav.download')}</span>
        </button>
        <NavLink to="/developers" className={({ isActive }) => [styles.navLink, isActive ? styles.navLinkActive : ''].join(' ')}>
          {t('nav.developers')}
        </NavLink>
        {/* on a phone the links above give way to one menu button beside the source */}
        <button
          type="button"
          className={styles.menuButton}
          aria-label={menu ? t('nav.closeMenu') : t('nav.menu')}
          aria-expanded={menu}
          aria-controls="phone-menu"
          data-testid="menu-button"
          onClick={() => {
            if (!menu) window.scrollTo({ top: 0 });
            setMenu(!menu);
          }}
        >
          <MenuGlyph open={menu} />
        </button>
        <a className={styles.source} href={SOURCE_URL} target="_blank" rel="noreferrer" aria-label={t('nav.sourceLabel')} data-testid="source-link">
          <GitHubMark size={18} />
          <span className={styles.sourceText}>{t('nav.source')}</span>
        </a>
      </nav>
      {menu ? (
        <PhoneMenu
          onClose={() => {
            setMenu(false);
          }}
          onDownload={() => {
            setDownload(true);
          }}
        />
      ) : null}
      {download ? (
        <DownloadDialog
          onClose={() => {
            setDownload(false);
          }}
        />
      ) : null}
    </header>
  );
}

function Heart(): React.JSX.Element {
  return (
    <svg className={styles.heart} width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 21s-7.6-4.6-10-9.3C.4 8.4 2.3 4.5 6 4.5c2.2 0 3.6 1.2 4.6 2.6.4.5 1.1.5 1.4 0 1-1.4 2.4-2.6 4.6-2.6 3.7 0 5.6 3.9 4 7.2C19.6 16.4 12 21 12 21z" />
    </svg>
  );
}

/**
 * The footer, one calm row: the appearance switch on the left, the signature —
 * a link to passwork.pro — on the right. What Bridge is
 * sits under the tool (Tagline); the app and the developers page are in the
 * header.

 */
function Footer(): React.JSX.Element {
  useT();
  return (
    <footer className={styles.footer}>
      <div className={styles.wrap}>
        <div className={styles.footerRow}>
          <div className={styles.footerStart}>
            <ThemeSwitch />
            <LanguageMenu />
          </div>
          <a className={styles.footerLove} href="https://passwork.pro" target="_blank" rel="noreferrer" data-testid="made-by">
            {tr('footer.madeWith', {
              heart: (children) => (
                <>
                  <Heart />
                  <span className="visually-hidden">{children}</span>
                </>
              ),
            })}
            <ArrowUpRightIcon size={13} className={styles.footerOut} />
          </a>
        </div>
      </div>
    </footer>
  );
}

/** What Bridge is, in one quiet line under the tool (not in the app, where the person already knows). */
function Tagline(): React.JSX.Element {
  const t = useT();
  return <p className={styles.tagline}>{t('tagline')}</p>;
}

/** `compact`: the app window — content is centred in a narrower column, like a tool rather than a landing page. */
export function Page({ children, compact = false }: { readonly children: ReactNode; readonly compact?: boolean }): React.JSX.Element {
  const t = useT();
  // inside another site's frame: just the tool on a clear background, and a quiet credit under it (lib/embed.ts)
  if (isEmbedded()) {
    return (
      <div className={styles.embed} data-layout-root>
        <main className={styles.embedMain}>{children}</main>
        <a className={styles.embedCredit} href="/" target="_blank" rel="noreferrer">
          <BridgeMark size={12} />
          {t('brand.full')}
        </a>
      </div>
    );
  }
  // the native app: the tool fills the window, as an app does — no brand, no links, no footer, but the site's dots; the settings sit
  // behind a gear in the corner (AppSettings) and, on the Mac, in the menu
  if (isApp()) {
    return (
      <div className={styles.appShell} data-layout-root>
        <DotField />
        <AppSettings />
        <main className={styles.appShellMain}>{children}</main>
      </div>
    );
  }
  return (
    <div className={styles.page} data-layout-root>
      <DotField />
      <div className={styles.wrap}>
        <Header />
      </div>
      <main className={[styles.wrap, styles.main, compact ? styles.mainCompact : ''].join(' ')}>
        {children}
        {compact ? <Tagline /> : null}
      </main>
      <Footer />
    </div>
  );
}
